import React, { useState, useEffect, useCallback } from 'react';
import MainLayout from '../../components/ui/MainLayout';
import Icon from '../../components/AppIcon';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { useRBAC } from '../../contexts/RBACContext';
import { logAuditAction } from '../../utils/auditLogger';

// Static fallback contacts (used if DB table doesn't exist yet)
const STATIC_CONTACTS = [
  { id: 'security', category: 'emergency', name: 'Security Office', role: 'Campus Security', phone: '+221 33 849 00 40', altPhone: '+221 77 000 00 40', email: 'security@usenghor.org', hours: '24/7', icon: 'Shield', color: 'bg-red-500', borderColor: 'border-red-200 dark:border-red-800', bgColor: 'bg-red-50 dark:bg-red-900/20', isEmergency: true, procedures: ['For immediate threats, call this number first', 'Security patrols campus every 30 minutes', 'CCTV monitoring active 24/7', 'Emergency response time: under 5 minutes'] },
  { id: 'medical', category: 'emergency', name: 'Medical Emergency', role: 'Campus Medical Services', phone: '+221 33 849 00 50', altPhone: '+221 15 (National Emergency)', email: 'medical@usenghor.org', hours: 'Mon-Fri 8AM-6PM · Emergency 24/7', icon: 'HeartPulse', color: 'bg-rose-500', borderColor: 'border-rose-200 dark:border-rose-800', bgColor: 'bg-rose-50 dark:bg-rose-900/20', isEmergency: true, procedures: ['For life-threatening emergencies, call 15 (national)', 'On-site nurse available during office hours', 'First aid kits located at each building entrance', 'Nearest hospital: Hôpital Principal (10 min)'] },
  { id: 'admin', category: 'administration', name: 'Residence Administration', role: 'Residence Management', phone: '+221 33 849 00 20', altPhone: null, email: 'residence@usenghor.org', hours: 'Mon-Fri 8AM-5PM', icon: 'Landmark', color: 'bg-purple-500', borderColor: 'border-purple-200 dark:border-purple-800', bgColor: 'bg-purple-50 dark:bg-purple-900/20', isEmergency: false, procedures: ['Room assignments and transfers', 'Reservation approvals and extensions', 'Financial matters and documentation', 'General residence inquiries'] },
  { id: 'maintenance', category: 'services', name: 'Maintenance Team', role: 'Facilities & Maintenance', phone: '+221 33 849 00 80', altPhone: '+221 77 000 00 80', email: 'maintenance@usenghor.org', hours: 'Mon-Sat 7AM-8PM · Emergency 24/7', icon: 'Wrench', color: 'bg-amber-500', borderColor: 'border-amber-200 dark:border-amber-800', bgColor: 'bg-amber-50 dark:bg-amber-900/20', isEmergency: false, procedures: ['Submit tickets via the Maintenance module for non-urgent issues', 'For water leaks or electrical hazards, call immediately', 'Gas emergencies: evacuate first, then call', 'Response time: urgent 1hr, standard 24-48hrs'] },
  { id: 'it', category: 'services', name: 'IT Support', role: 'Information Technology', phone: '+221 33 849 00 70', altPhone: null, email: 'it@usenghor.org', hours: 'Mon-Fri 8AM-6PM', icon: 'Monitor', color: 'bg-cyan-500', borderColor: 'border-cyan-200 dark:border-cyan-800', bgColor: 'bg-cyan-50 dark:bg-cyan-900/20', isEmergency: false, procedures: ['WiFi network: USenghor-Residence', 'Password resets and account issues', 'Device and software support', 'Report cybersecurity incidents immediately'] },
  { id: 'restaurant', category: 'services', name: 'Restaurant Management', role: 'Dining Services', phone: '+221 33 849 00 10', altPhone: null, email: 'restaurant@usenghor.org', hours: 'Breakfast 7-9AM · Lunch 12-2PM · Dinner 7-9PM', icon: 'UtensilsCrossed', color: 'bg-orange-500', borderColor: 'border-orange-200 dark:border-orange-800', bgColor: 'bg-orange-50 dark:bg-orange-900/20', isEmergency: false, procedures: ['Meal card issues and reloading', 'Dietary requirements and allergies', 'Special event catering requests', 'Food quality complaints'] },
];

const EMERGENCY_PROCEDURES = [
  { title: 'Fire Emergency', icon: 'Flame', color: 'text-red-500', steps: ['Activate the nearest fire alarm', 'Evacuate via the nearest staircase (not elevator)', 'Assemble at the designated muster point', 'Call Security: +221 33 849 00 40'] },
  { title: 'Medical Emergency', icon: 'HeartPulse', color: 'text-rose-500', steps: ['Call Medical Services: +221 33 849 00 50', 'Do not move the injured person unless in danger', 'Stay with the person until help arrives', 'National emergency: 15'] },
  { title: 'Power Outage', icon: 'Zap', color: 'text-amber-500', steps: ['Emergency lighting will activate automatically', 'Do not use candles or open flames', 'Report to Maintenance: +221 33 849 00 80', 'Avoid using elevators during outages'] },
  { title: 'Security Threat', icon: 'AlertTriangle', color: 'text-orange-500', steps: ['Call Security immediately: +221 33 849 00 40', 'Lock your door and stay inside if safe', 'Do not confront the threat', 'Follow instructions from security personnel'] },
];

const ICON_OPTIONS = ['Shield', 'HeartPulse', 'Landmark', 'Wrench', 'Monitor', 'UtensilsCrossed', 'Phone', 'Mail', 'Building2', 'Users', 'Wifi', 'Car', 'BookOpen', 'AlertTriangle'];
const COLOR_OPTIONS = ['bg-red-500', 'bg-rose-500', 'bg-purple-500', 'bg-amber-500', 'bg-cyan-500', 'bg-orange-500', 'bg-blue-500', 'bg-green-500', 'bg-indigo-500', 'bg-teal-500'];

const EMPTY_CONTACT = { name: '', role: '', phone: '', altPhone: '', email: '', hours: '', icon: 'Phone', color: 'bg-blue-500', borderColor: 'border-blue-200 dark:border-blue-800', bgColor: 'bg-blue-50 dark:bg-blue-900/20', isEmergency: false, category: 'services', procedures: [] };

export default function EmergencyContacts() {
  const { user } = useAuth();
  const { isAdmin } = useRBAC();
  const [contacts, setContacts] = useState(STATIC_CONTACTS);
  const [useDB, setUseDB] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [activeTab, setActiveTab] = useState('contacts');

  // Admin state
  const [editingContact, setEditingContact] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_CONTACT);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  const fetchContacts = useCallback(async () => {
    try {
      const { data, error } = await supabase?.from('contacts')?.select('*')?.order('is_emergency', { ascending: false })?.order('name');
      if (!error && data && data?.length > 0) {
        setContacts(data?.map(c => ({
          ...c,
          isEmergency: c?.is_emergency,
          borderColor: c?.border_color || 'border-border',
          bgColor: c?.bg_color || 'bg-muted/30',
          procedures: c?.procedures || [],
        })));
        setUseDB(true);
      }
    } catch (err) {
      // Table may not exist yet, use static data
    }
  }, []);

  useEffect(() => { fetchContacts(); }, [fetchContacts]);

  const emergencyContacts = contacts?.filter(c => c?.isEmergency || c?.is_emergency);
  const serviceContacts = contacts?.filter(c => !c?.isEmergency && !c?.is_emergency);

  const handleAddNew = () => {
    setEditForm({ ...EMPTY_CONTACT });
    setEditingContact('new');
    setSaveError('');
  };

  const handleEdit = (contact) => {
    setEditForm({
      name: contact?.name || '',
      role: contact?.role || '',
      phone: contact?.phone || '',
      altPhone: contact?.altPhone || contact?.alt_phone || '',
      email: contact?.email || '',
      hours: contact?.hours || '',
      icon: contact?.icon || 'Phone',
      color: contact?.color || 'bg-blue-500',
      borderColor: contact?.borderColor || contact?.border_color || 'border-blue-200 dark:border-blue-800',
      bgColor: contact?.bgColor || contact?.bg_color || 'bg-blue-50 dark:bg-blue-900/20',
      isEmergency: contact?.isEmergency || contact?.is_emergency || false,
      category: contact?.category || 'services',
      procedures: contact?.procedures || [],
    });
    setEditingContact(contact);
    setSaveError('');
  };

  const handleSave = async () => {
    if (!editForm?.name?.trim() || !editForm?.phone?.trim()) {
      setSaveError('Name and phone are required.');
      return;
    }
    setSaving(true);
    setSaveError('');
    try {
      const payload = {
        name: editForm?.name,
        role: editForm?.role,
        phone: editForm?.phone,
        alt_phone: editForm?.altPhone || null,
        email: editForm?.email,
        hours: editForm?.hours,
        icon: editForm?.icon,
        color: editForm?.color,
        border_color: editForm?.borderColor,
        bg_color: editForm?.bgColor,
        is_emergency: editForm?.isEmergency,
        category: editForm?.category,
        procedures: editForm?.procedures,
      };
      if (editingContact === 'new') {
        const { data, error } = await supabase?.from('contacts')?.insert(payload)?.select()?.single();
        if (error) throw error;
        await logAuditAction({ action: 'CREATE', module: 'contacts', recordId: data?.id, recordSummary: `Added contact: "${editForm?.name}"`, user });
      } else {
        const { error } = await supabase?.from('contacts')?.update(payload)?.eq('id', editingContact?.id);
        if (error) throw error;
        await logAuditAction({ action: 'UPDATE', module: 'contacts', recordId: editingContact?.id, recordSummary: `Updated contact: "${editForm?.name}"`, user });
      }
      setEditingContact(null);
      fetchContacts();
    } catch (err) {
      // If contacts table doesn't exist, update local state
      if (editingContact === 'new') {
        const newContact = { ...editForm, id: Date.now()?.toString(), isEmergency: editForm?.isEmergency };
        setContacts(prev => [...prev, newContact]);
      } else {
        setContacts(prev => prev?.map(c => c?.id === editingContact?.id ? { ...c, ...editForm } : c));
      }
      setEditingContact(null);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (contactId, contactName) => {
    try {
      if (useDB) {
        const { error } = await supabase?.from('contacts')?.delete()?.eq('id', contactId);
        if (error) throw error;
        await logAuditAction({ action: 'DELETE', module: 'contacts', recordId: contactId, recordSummary: `Deleted contact: "${contactName}"`, user });
        fetchContacts();
      } else {
        setContacts(prev => prev?.filter(c => c?.id !== contactId));
      }
      setDeleteConfirmId(null);
    } catch (err) {
      console.error('Delete contact error:', err);
      setContacts(prev => prev?.filter(c => c?.id !== contactId));
      setDeleteConfirmId(null);
    }
  };

  return (
    <MainLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-foreground" style={{ fontFamily: 'Manrope, sans-serif' }}>Contacts</h1>
            <p className="text-sm text-muted-foreground mt-1">Important contacts and emergency procedures</p>
          </div>
          {/* Admin Add Contact button */}
          {isAdmin && (
            <button
              onClick={handleAddNew}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 transition-opacity"
            >
              <Icon name="Plus" size={16} />
              Add Contact
            </button>
          )}
        </div>

        {/* Emergency Banner */}
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4 flex items-center gap-4">
          <div className="w-10 h-10 rounded-full bg-red-500 flex items-center justify-center flex-shrink-0">
            <Icon name="AlertTriangle" size={20} className="text-white" />
          </div>
          <div className="flex-1">
            <div className="font-semibold text-red-700 dark:text-red-400 text-sm">In case of immediate danger</div>
            <div className="text-red-600 dark:text-red-300 text-xs mt-0.5">Call Security: <strong>+221 33 849 00 40</strong> · Medical Emergency: <strong>15</strong></div>
          </div>
          <div className="flex gap-2">
            <a href="tel:+221338490040" className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-500 text-white text-xs font-medium hover:bg-red-600 transition-colors">
              <Icon name="Phone" size={12} />
              Security
            </a>
            <a href="tel:15" className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-rose-500 text-white text-xs font-medium hover:bg-rose-600 transition-colors">
              <Icon name="HeartPulse" size={12} />
              Medical
            </a>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-muted/30 rounded-lg p-1 w-fit">
          {[
            { id: 'contacts', label: 'All Contacts', icon: 'Phone' },
            { id: 'procedures', label: 'Emergency Procedures', icon: 'BookOpen' },
          ]?.map(tab => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
                activeTab === tab?.id ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon name={tab?.icon} size={14} />
              {tab?.label}
            </button>
          ))}
        </div>

        {activeTab === 'contacts' && (
          <div className="space-y-6">
            {emergencyContacts?.length > 0 && (
              <div>
                <h2 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                  <Icon name="AlertCircle" size={14} className="text-red-500" />
                  Emergency Services
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {emergencyContacts?.map(contact => (
                    <ContactCard key={contact?.id} contact={contact} onSelect={setSelectedContact} isAdmin={isAdmin} onEdit={handleEdit} onDelete={(id) => setDeleteConfirmId(id)} />
                  ))}
                </div>
              </div>
            )}
            {serviceContacts?.length > 0 && (
              <div>
                <h2 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                  <Icon name="Building2" size={14} className="text-muted-foreground" />
                  Campus Services
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {serviceContacts?.map(contact => (
                    <ContactCard key={contact?.id} contact={contact} onSelect={setSelectedContact} isAdmin={isAdmin} onEdit={handleEdit} onDelete={(id) => setDeleteConfirmId(id)} />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'procedures' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {EMERGENCY_PROCEDURES?.map(proc => (
              <div key={proc?.title} className="bg-card border border-border rounded-xl p-5">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-9 h-9 rounded-xl bg-muted flex items-center justify-center">
                    <Icon name={proc?.icon} size={18} className={proc?.color} />
                  </div>
                  <h3 className="font-semibold text-foreground">{proc?.title}</h3>
                </div>
                <ol className="space-y-2">
                  {proc?.steps?.map((step, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm text-foreground">
                      <span className="flex-shrink-0 w-5 h-5 rounded-full bg-muted text-muted-foreground text-xs flex items-center justify-center font-bold mt-0.5">{i + 1}</span>
                      {step}
                    </li>
                  ))}
                </ol>
              </div>
            ))}
          </div>
        )}

        {/* Contact Detail Modal */}
        {selectedContact && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
            <div className="bg-card border border-border rounded-2xl w-full max-w-md">
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className={`w-12 h-12 rounded-xl ${selectedContact?.color} flex items-center justify-center`}>
                    <Icon name={selectedContact?.icon} size={22} className="text-white" />
                  </div>
                  <button onClick={() => setSelectedContact(null)} className="p-1 rounded-lg hover:bg-muted transition-colors">
                    <Icon name="X" size={18} className="text-muted-foreground" />
                  </button>
                </div>
                <h2 className="text-xl font-bold text-foreground mb-1">{selectedContact?.name}</h2>
                <p className="text-sm text-muted-foreground mb-5">{selectedContact?.role}</p>
                <div className="space-y-3 mb-5">
                  <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg">
                    <Icon name="Phone" size={16} className="text-muted-foreground" />
                    <div>
                      <div className="text-xs text-muted-foreground">Primary Phone</div>
                      <a href={`tel:${selectedContact?.phone}`} className="text-sm font-medium text-primary hover:underline">{selectedContact?.phone}</a>
                    </div>
                  </div>
                  {(selectedContact?.altPhone || selectedContact?.alt_phone) && (
                    <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg">
                      <Icon name="PhoneCall" size={16} className="text-muted-foreground" />
                      <div>
                        <div className="text-xs text-muted-foreground">Alternative / Emergency</div>
                        <a href={`tel:${selectedContact?.altPhone || selectedContact?.alt_phone}`} className="text-sm font-medium text-primary hover:underline">{selectedContact?.altPhone || selectedContact?.alt_phone}</a>
                      </div>
                    </div>
                  )}
                  {selectedContact?.email && (
                    <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg">
                      <Icon name="Mail" size={16} className="text-muted-foreground" />
                      <div>
                        <div className="text-xs text-muted-foreground">Email</div>
                        <a href={`mailto:${selectedContact?.email}`} className="text-sm font-medium text-primary hover:underline">{selectedContact?.email}</a>
                      </div>
                    </div>
                  )}
                  {selectedContact?.hours && (
                    <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg">
                      <Icon name="Clock" size={16} className="text-muted-foreground" />
                      <div>
                        <div className="text-xs text-muted-foreground">Available Hours</div>
                        <div className="text-sm text-foreground">{selectedContact?.hours}</div>
                      </div>
                    </div>
                  )}
                </div>
                {selectedContact?.procedures?.length > 0 && (
                  <div className="mb-5">
                    <div className="text-xs font-medium text-muted-foreground mb-2">When to contact</div>
                    <div className="space-y-1.5">
                      {selectedContact?.procedures?.map((p, i) => (
                        <div key={i} className="flex items-start gap-2 text-sm text-foreground">
                          <div className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5 flex-shrink-0" />
                          {p}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div className="flex gap-3">
                  <a href={`tel:${selectedContact?.phone}`} className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 transition-opacity">
                    <Icon name="Phone" size={14} />
                    Call Now
                  </a>
                  {selectedContact?.email && (
                    <a href={`mailto:${selectedContact?.email}`} className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-border text-sm text-foreground hover:bg-muted transition-colors">
                      <Icon name="Mail" size={14} />
                      Email
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Admin Edit/Add Modal */}
      {editingContact !== null && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="bg-card border border-border rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-lg font-bold text-foreground">{editingContact === 'new' ? 'Add Contact' : 'Edit Contact'}</h2>
                <button onClick={() => setEditingContact(null)} className="p-1 rounded-lg hover:bg-muted transition-colors">
                  <Icon name="X" size={18} className="text-muted-foreground" />
                </button>
              </div>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1">Name *</label>
                    <input type="text" value={editForm?.name} onChange={e => setEditForm(f => ({ ...f, name: e?.target?.value }))} placeholder="Contact name" className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1">Role / Title</label>
                    <input type="text" value={editForm?.role} onChange={e => setEditForm(f => ({ ...f, role: e?.target?.value }))} placeholder="e.g. Campus Security" className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1">Phone *</label>
                    <input type="text" value={editForm?.phone} onChange={e => setEditForm(f => ({ ...f, phone: e?.target?.value }))} placeholder="+221 33 849 00 00" className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1">Alt Phone</label>
                    <input type="text" value={editForm?.altPhone} onChange={e => setEditForm(f => ({ ...f, altPhone: e?.target?.value }))} placeholder="Optional" className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1">Email</label>
                    <input type="email" value={editForm?.email} onChange={e => setEditForm(f => ({ ...f, email: e?.target?.value }))} placeholder="contact@usenghor.org" className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1">Hours</label>
                    <input type="text" value={editForm?.hours} onChange={e => setEditForm(f => ({ ...f, hours: e?.target?.value }))} placeholder="Mon-Fri 8AM-5PM" className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1">Icon</label>
                  <div className="flex flex-wrap gap-2">
                    {ICON_OPTIONS?.map(ico => (
                      <button key={ico} onClick={() => setEditForm(f => ({ ...f, icon: ico }))} className={`p-2 rounded-lg border transition-all ${editForm?.icon === ico ? 'border-primary bg-primary/10' : 'border-border hover:border-primary/50'}`}>
                        <Icon name={ico} size={16} className={editForm?.icon === ico ? 'text-primary' : 'text-muted-foreground'} />
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1">Color</label>
                  <div className="flex flex-wrap gap-2">
                    {COLOR_OPTIONS?.map(col => (
                      <button key={col} onClick={() => setEditForm(f => ({ ...f, color: col }))} className={`w-8 h-8 rounded-lg ${col} border-2 transition-all ${editForm?.color === col ? 'border-foreground scale-110' : 'border-transparent'}`} />
                    ))}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <input type="checkbox" id="is-emergency" checked={editForm?.isEmergency} onChange={e => setEditForm(f => ({ ...f, isEmergency: e?.target?.checked }))} className="rounded" />
                  <label htmlFor="is-emergency" className="text-sm text-foreground">Emergency contact (shown in emergency section)</label>
                </div>
                {saveError && (
                  <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-600 text-sm">
                    <Icon name="AlertCircle" size={14} />
                    {saveError}
                  </div>
                )}
              </div>
              <div className="flex gap-3 mt-5">
                <button onClick={handleSave} disabled={saving} className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50">
                  {saving ? <Icon name="Loader2" size={14} className="animate-spin" /> : <Icon name="Save" size={14} />}
                  {saving ? 'Saving...' : 'Save Contact'}
                </button>
                <button onClick={() => setEditingContact(null)} className="px-4 py-2 rounded-lg border border-border text-sm text-muted-foreground hover:text-foreground transition-colors">Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {deleteConfirmId && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="bg-card border border-border rounded-2xl w-full max-w-sm p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                <Icon name="Trash2" size={18} className="text-red-500" />
              </div>
              <div>
                <h3 className="font-semibold text-foreground">Delete Contact</h3>
                <p className="text-sm text-muted-foreground">This action cannot be undone.</p>
              </div>
            </div>
            <div className="flex gap-3">
              <button onClick={() => { const c = contacts?.find(x => x?.id === deleteConfirmId); handleDelete(deleteConfirmId, c?.name || ''); }} className="flex-1 px-4 py-2 rounded-lg bg-red-500 text-white text-sm font-medium hover:bg-red-600 transition-colors">Delete</button>
              <button onClick={() => setDeleteConfirmId(null)} className="flex-1 px-4 py-2 rounded-lg border border-border text-sm text-muted-foreground hover:text-foreground transition-colors">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </MainLayout>
  );
}

function ContactCard({ contact, onSelect, isAdmin, onEdit, onDelete }) {
  return (
    <div className={`bg-card border rounded-xl p-4 ${contact?.isEmergency || contact?.is_emergency ? contact?.borderColor || 'border-red-200 dark:border-red-800' : 'border-border'}`}>
      <div className="flex items-start gap-3 mb-3">
        <div className={`w-10 h-10 rounded-xl ${contact?.color} flex items-center justify-center flex-shrink-0`}>
          <Icon name={contact?.icon} size={18} className="text-white" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-foreground text-sm">{contact?.name}</div>
          <div className="text-xs text-muted-foreground">{contact?.role}</div>
        </div>
        {/* Admin inline actions */}
        {isAdmin && (
          <div className="flex items-center gap-1 flex-shrink-0">
            <button onClick={() => onEdit(contact)} title="Edit" className="p-1.5 rounded-lg text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors">
              <Icon name="Pencil" size={13} />
            </button>
            <button onClick={() => onDelete(contact?.id)} title="Delete" className="p-1.5 rounded-lg text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
              <Icon name="Trash2" size={13} />
            </button>
          </div>
        )}
      </div>
      <div className="space-y-1.5 mb-3">
        {contact?.hours && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Icon name="Clock" size={11} />
            <span>{contact?.hours}</span>
          </div>
        )}
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Icon name="Phone" size={11} />
          <span>{contact?.phone}</span>
        </div>
      </div>
      <div className="flex gap-2">
        <a href={`tel:${contact?.phone}`} className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${contact?.isEmergency || contact?.is_emergency ? 'bg-red-500 text-white hover:bg-red-600' : 'bg-primary text-primary-foreground hover:opacity-90'}`}>
          <Icon name="Phone" size={11} />
          Call
        </a>
        <button onClick={() => onSelect(contact)} className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg border border-border text-xs text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">
          <Icon name="Info" size={11} />
          Details
        </button>
      </div>
    </div>
  );
}
